import { formatChatError } from "./formatChatError";
import { useUnsavedChanges } from "../../layout/AppShellContext";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MobileMenu } from "../Dashboard/MobileMenu";
import {
  chatApi,
  ChatItem,
  ChatAttachment,
  ChatMessage,
  ChatPinnedMessage,
  ChatPreferences,
  ChatSettings,
  ChatReadState,
  ChatTypingState,
} from "../../api/chat.api";
import { api } from "../../api/client";
import { openApiEventStream } from "../../api/sse";
import "./ChatsPage.scss";
import AssistantConversation from "./AssistantConversation";

type MeResponse = {
  id: number;
  phone: string | null;
  name: string | null;
  email: string | null;
  roles: string[];
  primaryRole: string;
  avatarUrl: string | null;
  companies: { id: number; name: string | null; address: string | null }[];
};

type ChatStreamPayload = {
  messages?: ChatMessage[];
  reads?: ChatReadState[];
  typing?: ChatTypingState[];
  pinnedMessages?: ChatPinnedMessage[];
  cursor?: {
    lastMessageId?: number;
    serverTime?: string;
  };
};

type SettingsScreen = "root" | "appearance";
type MutePreset = "1h" | "8h" | "tomorrow" | "forever";
type MessageGestureRef = {
  id: number;
  isMine: boolean;
  pointerId: number;
  startX: number;
  startY: number;
  longPressTimer: number | null;
};
type MessageSwipeState = {
  id: number;
  offset: number;
  ready: boolean;
};

const SWIPE_REPLY_THRESHOLD = 54;
const LONG_PRESS_DELAY = 430;

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function formatDateLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
}

function formatListTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return formatTime(iso);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
}

function buildChatName(chat: ChatItem): string {
  if (chat.title && chat.title.trim()) return chat.title.trim();

  const nonMe = chat.participants.filter((p) => !p.isMe);
  if (nonMe.length === 0) return "Личный чат";

  const labels = nonMe.map((p) => p.displayName || p.phone).filter((v): v is string => Boolean(v));
  return labels.join(", ") || "Новый чат";
}

function upsertMessages(prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  if (incoming.length === 0) return prev;
  const map = new Map<number, ChatMessage>();
  prev.forEach((m) => map.set(m.id, m));
  incoming.forEach((m) => map.set(m.id, m));
  return Array.from(map.values()).sort((a, b) => a.id - b.id);
}

function getLatestMessageId(items: ChatMessage[]): number {
  return items.reduce((maxId, message) => Math.max(maxId, Number(message.id || 0)), 0);
}

function sortChats(items: ChatItem[]): ChatItem[] {
  return [...items].sort((a, b) => {
    if (Boolean(a.isPinned) !== Boolean(b.isPinned)) return a.isPinned ? -1 : 1;
    if (a.isPinned && b.isPinned) {
      return +new Date(b.pinnedAt || b.updatedAt) - +new Date(a.pinnedAt || a.updatedAt);
    }
    return +new Date(b.updatedAt) - +new Date(a.updatedAt);
  });
}

function getMessagePreview(message?: ChatMessage | null): string {
  if (!message) return "Нет сообщений";
  if (message.isDeleted) return "Сообщение удалено";
  if ((message.text || "").trim()) return message.text.trim();
  if (message.attachments?.length) {
    const kind = message.attachments[0].kind;
    if (kind === "image" || kind === "video") return "Фото или видео";
    if (kind === "voice") return "Голосовое";
    return "Файл";
  }
  return "Сообщение";
}

function getInitials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "CH";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function isMobileChatViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 760px)").matches;
}

function buildMutedUntil(preset: MutePreset): string | null {
  if (preset === "forever") return null;

  const next = new Date();
  if (preset === "1h") next.setHours(next.getHours() + 1);
  if (preset === "8h") next.setHours(next.getHours() + 8);
  if (preset === "tomorrow") {
    next.setDate(next.getDate() + 1);
    next.setHours(9, 0, 0, 0);
  }

  return next.toISOString();
}

function formatMuteStatus(isMuted?: boolean, mutedUntil?: string | null): string {
  if (!isMuted) return "Включены";
  if (!mutedUntil) return "Отключены";

  const date = new Date(mutedUntil);
  if (Number.isNaN(date.getTime())) return "Отключены";

  return `До ${date.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" })}, ${formatTime(mutedUntil)}`;
}

function mediaCountLabel(count?: number): string {
  return `${Math.max(0, Number(count || 0))}`;
}



type IconName =
  | "search" | "plus" | "pin" | "star" | "attach" | "smile" | "send" | "more" | "moon" | "sun" | "down" | "close" | "back" | "menu"
  | "user" | "phone" | "chat" | "gear" | "image" | "camera" | "file" | "map" | "contact" | "poll" | "audio" | "video" | "trash"
  | "reply" | "copy" | "edit" | "forward" | "select";

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, React.ReactNode> = {
    search: <><circle cx="11" cy="11" r="6" /><path d="M16 16l4 4" /></>,
    plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
    pin: <><path d="M9 4l11 11" /><path d="M14 5l5 5" /><path d="M5 19l6-6" /><path d="M9 4l-2 6 7 7 6-2" /></>,
    star: <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.1-5.6-2.9-5.6 2.9 1.1-6.1L3 9.6l6.2-.9L12 3z" />,
    attach: <path d="M20 11.5l-7.4 7.4a5 5 0 0 1-7.1-7.1l8.1-8.1a3.4 3.4 0 0 1 4.8 4.8l-8.1 8.1a1.8 1.8 0 0 1-2.5-2.5l7.4-7.4" />,
    smile: <><circle cx="12" cy="12" r="9" /><path d="M8 10h.01" /><path d="M16 10h.01" /><path d="M8.5 14.5a5 5 0 0 0 7 0" /></>,
    send: <><path d="M21 3L10 14" /><path d="M21 3l-7 18-4-7-7-4 18-7z" /></>,
    more: <><circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" /></>,
    moon: <path d="M21 14.5A8.5 8.5 0 0 1 9.5 3 7 7 0 1 0 21 14.5z" />,
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="M4.93 4.93l1.41 1.41" /><path d="M17.66 17.66l1.41 1.41" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="M4.93 19.07l1.41-1.41" /><path d="M17.66 6.34l1.41-1.41" /></>,
    down: <path d="M6 9l6 6 6-6" />,
    close: <><path d="M6 6l12 12" /><path d="M18 6L6 18" /></>,
    back: <><path d="M15 18l-6-6 6-6" /><path d="M9 12h12" /></>,
    menu: <><path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" /></>,
    user: <><path d="M20 21a8 8 0 0 0-16 0" /><circle cx="12" cy="7" r="4" /></>,
    phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6.4 6.4l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" />,
    chat: <><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1A2 2 0 1 1 4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.6-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1A2 2 0 1 1 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3h.1A1.7 1.7 0 0 0 10 3V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1A2 2 0 1 1 19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1A1.7 1.7 0 0 0 21 10h0a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>,
    image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="M21 15l-5-5L5 20" /></>,
    camera: <><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l2-3h8l2 3h3a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>,
    file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>,
    map: <><path d="M12 21s7-4.4 7-11a7 7 0 1 0-14 0c0 6.6 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></>,
    contact: <><path d="M17 21v-2a4 4 0 0 0-8 0v2" /><circle cx="12" cy="8" r="4" /><path d="M4 4h16v18H4z" /></>,
    poll: <><path d="M6 20V10" /><path d="M12 20V4" /><path d="M18 20v-7" /></>,
    audio: <><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></>,
    video: <><rect x="3" y="6" width="13" height="12" rx="2" /><path d="M16 10l5-3v10l-5-3z" /></>,
    trash: <><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 16H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></>,
    reply: <><path d="M9 17l-6-5 6-5" /><path d="M3 12h12a6 6 0 0 1 6 6v1" /></>,
    copy: <><rect x="9" y="9" width="13" height="13" rx="2" /><rect x="2" y="2" width="13" height="13" rx="2" /></>,
    edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
    forward: <><path d="M15 17l6-5-6-5" /><path d="M21 12H9a6 6 0 0 0-6 6v1" /></>,
    select: <><circle cx="12" cy="12" r="9" /><path d="M8 12l2.5 2.5L16 9" /></>,
  };

  return (
    <svg className="chat-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {paths[name]}
    </svg>
  );
}

function isParticipantOnline(lastSeenAt?: string | null): boolean {
  if (!lastSeenAt) return false;
  const seen = new Date(lastSeenAt).getTime();
  if (Number.isNaN(seen)) return false;
  return Date.now() - seen < 5 * 60 * 1000;
}

function isChatOnline(chat: ChatItem): boolean {
  return chat.participants.some((participant) => !participant.isMe && isParticipantOnline(participant.lastSeenAt));
}

function formatFileSize(size: number): string {
  if (!size) return "0 Б";
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${Math.round(size / 102.4) / 10} КБ`;
  return `${Math.round(size / 104857.6) / 10} МБ`;
}

function renderInlineText(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const pattern = /(`[^`]+`|https?:\/\/[^\s<]+)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const value = match[0];
    if (value.startsWith("`")) {
      nodes.push(<code key={`code-${match.index}`}>{value.slice(1, -1)}</code>);
    } else {
      nodes.push(<a key={`link-${match.index}`} href={value} target="_blank" rel="noreferrer">{value}</a>);
    }
    lastIndex = match.index + value.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

function MessageText({ text }: { text: string }) {
  const parts = (text || "").split(/(```[\s\S]*?```)/g);

  return (
    <div className="message-text">
      {parts.map((part, partIndex) => {
        if (part.startsWith("```") && part.endsWith("```")) {
          return <pre key={partIndex}><code>{part.slice(3, -3).trim()}</code></pre>;
        }

        const lines = part.split("\n");
        return lines.map((line, lineIndex) => (
          <React.Fragment key={`${partIndex}-${lineIndex}`}>
            {renderInlineText(line)}
            {lineIndex < lines.length - 1 && <br />}
          </React.Fragment>
        ));
      })}
    </div>
  );
}

function AttachmentView({
  attachment,
  onOpenImage,
}: {
  attachment: ChatAttachment;
  onOpenImage?: (preview: { url: string; title: string }) => void;
}) {
  const url = chatApi.resolveAssetUrl(attachment.url);
  if (attachment.kind === "image") {
    return (
      <button
        className="attachment-card media media-button"
        type="button"
        onClick={() => onOpenImage?.({ url, title: attachment.originalName || "Изображение" })}
        aria-label="Открыть изображение"
      >
        <img src={url} alt={attachment.originalName || "Изображение"} />
      </button>
    );
  }
  if (attachment.kind === "video") {
    return <video className="attachment-card media" src={url} controls playsInline />;
  }
  if (attachment.kind === "voice") {
    return <audio className="attachment-card voice" src={url} controls preload="metadata" />;
  }
  return <a className="attachment-card file" href={url} target="_blank" rel="noreferrer">{attachment.originalName || "Файл"}</a>;
}

function ChatListSkeleton() {
  return (
    <div className="chat-list-skeleton" aria-label="Загрузка чатов">
      {Array.from({ length: 8 }).map((_, index) => (
        <div className="chat-skeleton-row" key={index}>
          <span className="skeleton-circle" />
          <span className="skeleton-lines">
            <i className="skeleton-line title" />
            <i className="skeleton-line preview" />
          </span>
          <i className="skeleton-line time" />
        </div>
      ))}
    </div>
  );
}

function MessageListSkeleton() {
  return (
    <div className="message-list-skeleton" aria-label="Загрузка сообщений">
      <span className="skeleton-date-pill" />
      {Array.from({ length: 7 }).map((_, index) => {
        const mine = index % 3 === 1;
        const wide = index % 4 === 0;
        return (
          <div className={`message-skeleton-bubble ${mine ? "mine" : "other"} ${wide ? "wide" : ""}`} key={index}>
            <i className="skeleton-line message-line" />
            <i className="skeleton-line message-line short" />
          </div>
        );
      })}
    </div>
  );
}

export const ChatsPage: React.FC = () => {
  const navigate = useNavigate();
  const { chatId: routeChatId } = useParams<{ chatId?: string }>();
  const assistantSelected = routeChatId === "assistant";
  const routeSelectedChatId = useMemo(() => {
    if (!routeChatId) return null;
    const value = Number(routeChatId);
    return Number.isInteger(value) && value > 0 ? value : null;
  }, [routeChatId]);
  const routeChatIdIsInvalid = Boolean(routeChatId && !assistantSelected && routeSelectedChatId === null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSidebarScreen, setMobileSidebarScreen] = useState<"chats" | "settings">("chats");
  const [settingsScreen, setSettingsScreen] = useState<SettingsScreen>("root");

  const [loadingChats, setLoadingChats] = useState(true);
  const [chatsError, setChatsError] = useState<string | null>(null);
  const [selectedChatAccessError, setSelectedChatAccessError] = useState<string | null>(null);
  const [chats, setChats] = useState<ChatItem[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [streamReadyChatId, setStreamReadyChatId] = useState<number | null>(null);

  const [readStates, setReadStates] = useState<Record<number, ChatReadState>>({});
  const [typingStates, setTypingStates] = useState<ChatTypingState[]>([]);

  const [query, setQuery] = useState("");
  const [listFilter, setListFilter] = useState<"all" | "unread" | "important">("all");
  const [messageDraft, setMessageDraft] = useState("");
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [replyingToMessageId, setReplyingToMessageId] = useState<number | null>(null);
  const [activeMessageAction, setActiveMessageAction] = useState<ChatMessage | null>(null);
  const [pinnedMessages, setPinnedMessages] = useState<ChatPinnedMessage[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isChatSettingsOpen, setIsChatSettingsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ChatMessage[]>([]);
  const [forwardingMessageId, setForwardingMessageId] = useState<number | null>(null);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [isAttachmentSheetOpen, setIsAttachmentSheetOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [activeImagePreview, setActiveImagePreview] = useState<{ url: string; title: string } | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showJumpButton, setShowJumpButton] = useState(false);
  const [messageSwipe, setMessageSwipe] = useState<MessageSwipeState | null>(null);
  useUnsavedChanges(!!messageDraft.trim() || pendingFiles.length > 0 || uploadBusy, "Сообщение в чате");
  const [themeMode, setThemeMode] = useState<"light" | "dark">(() => {
    if (typeof window === "undefined") return "light";
    return window.localStorage.getItem("lkfr-chat-theme-v2") === "dark" ? "dark" : "light";
  });
  const [me, setMe] = useState<MeResponse | null>(null);
  const [meLoading, setMeLoading] = useState(false);
  const [meError, setMeError] = useState<string | null>(null);
  const [chatSettings, setChatSettings] = useState<ChatSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [mutePreset, setMutePreset] = useState<MutePreset>("8h");

  const [createPhone, setCreatePhone] = useState("");
  const [createTitle, setCreateTitle] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [invitePhone, setInvitePhone] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const streamCursorRef = useRef(0);
  const messagesPanelRef = useRef<HTMLElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const typingIdleTimerRef = useRef<number | null>(null);
  const typingSentRef = useRef(false);
  const lastReadSentRef = useRef<number>(0);
  const shouldStickToBottomRef = useRef(true);
  const messageGestureRef = useRef<MessageGestureRef | null>(null);

  const selectedChat = useMemo(
    () => chats.find((c) => c.id === selectedChatId) ?? null,
    [chats, selectedChatId]
  );

  const myUserId = useMemo(() => {
    if (!selectedChat) return null;
    const me = selectedChat.participants.find((p) => p.isMe);
    return me?.userId ?? null;
  }, [selectedChat]);

  const editingMessage = useMemo(
    () => messages.find((message) => message.id === editingMessageId) ?? null,
    [editingMessageId, messages]
  );

  const replyingToMessage = useMemo(
    () => messages.find((message) => message.id === replyingToMessageId) ?? null,
    [replyingToMessageId, messages]
  );

  const latestPinnedMessage = pinnedMessages[0] ?? null;

  const imageAttachments = useMemo(() => {
    return messages.flatMap((message) =>
      (message.attachments || [])
        .filter((attachment) => attachment.kind === "image")
        .map((attachment) => ({
          id: attachment.id,
          url: chatApi.resolveAssetUrl(attachment.url),
          title: attachment.originalName || "Изображение",
          author: selectedChat ? buildChatName(selectedChat) : "Чат",
          createdAt: message.createdAt,
        }))
    );
  }, [messages, selectedChat]);

  const activeImageIndex = activeImagePreview
    ? imageAttachments.findIndex((item) => item.url === activeImagePreview.url)
    : -1;

  const typingUserIds = useMemo(() => {
    const ids = typingStates
      .filter((t) => t.isTyping && t.userId !== null)
      .map((t) => t.userId as number);
    return Array.from(new Set(ids));
  }, [typingStates]);

  const filteredChats = useMemo(() => {
    const q = query.trim().toLowerCase();

    return sortChats(chats).filter((chat) => {
      if (listFilter === "unread" && !chat.unreadCount) return false;
      if (listFilter === "important" && !chat.isImportant) return false;
      if (!q) return true;
      const name = buildChatName(chat).toLowerCase();
      const phones = chat.participants.map((p) => p.phone ?? "").join(" ").toLowerCase();
      return name.includes(q) || phones.includes(q);
    });
  }, [chats, listFilter, query]);

  const loadChats = useCallback(async () => {
    setLoadingChats(true);
    setChatsError(null);
    setSelectedChatAccessError(null);
    try {
      const list = await chatApi.listChats();
      let nextChats = sortChats(list);

      if (assistantSelected) {
        setChats(nextChats);
        setSelectedChatId(null);
        setMobileDialogOpen(true);
        setSelectedChatAccessError(null);
        return;
      }

      if (routeChatIdIsInvalid) {
        setChats(nextChats);
        setSelectedChatId(null);
        setMobileDialogOpen(false);
        setSelectedChatAccessError("Некорректная ссылка на чат");
        return;
      }

      if (routeSelectedChatId) {
        let targetChat = nextChats.find((chat) => chat.id === routeSelectedChatId) ?? null;

        if (!targetChat) {
          try {
            targetChat = await chatApi.getChat(routeSelectedChatId);
            nextChats = sortChats([targetChat, ...nextChats.filter((chat) => chat.id !== targetChat?.id)]);
          } catch (e: any) {
            setChats(nextChats);
            setSelectedChatId(null);
            setMobileDialogOpen(false);
            setSelectedChatAccessError(formatChatError(e, "Чат не найден или у вас нет доступа"));
            return;
          }
        }

        setChats(nextChats);
        setSelectedChatId(targetChat.id);
        setMobileDialogOpen(true);
        return;
      }

      const firstChatId = nextChats[0]?.id ?? null;
      setChats(nextChats);
      if (isMobileChatViewport()) {
        setSelectedChatId(null);
        setMobileDialogOpen(false);
        return;
      }

      setSelectedChatId(firstChatId);
      if (firstChatId) {
        navigate(`/chats/${firstChatId}`, { replace: true });
      }
    } catch (e: any) {
      const message = formatChatError(e, "Не удалось загрузить чаты");
      setChatsError(message);
      setToastMessage(message);
      window.setTimeout(() => setToastMessage(""), 2400);
    } finally {
      setLoadingChats(false);
    }
  }, [navigate, routeChatIdIsInvalid, routeSelectedChatId, assistantSelected]);

  const loadMessages = useCallback(async (chatId: number, beforeId?: number) => {
    if (!chatId) return;

    if (!beforeId) {
      setMessagesLoading(true);
      setMessagesError(null);
      setReadStates({});
      setTypingStates([]);
      setPinnedMessages([]);
      lastReadSentRef.current = 0;
    }

    try {
      const data = await chatApi.listMessages(chatId, { limit: 30, beforeId });
      if (beforeId) {
        setMessages((prev) => upsertMessages(prev, data.items));
      } else {
        setMessages(data.items);
        streamCursorRef.current = getLatestMessageId(data.items);
        setStreamReadyChatId(chatId);
        void chatApi.listPins(chatId).then(setPinnedMessages).catch(() => undefined);
      }
      setHasMore(data.hasMore);
    } catch (e: any) {
      setMessagesError(formatChatError(e, "Не удалось загрузить сообщения"));
    } finally {
      if (!beforeId) setMessagesLoading(false);
    }
  }, []);

  const stopTyping = useCallback(async () => {
    if (!selectedChatId || !typingSentRef.current) return;
    typingSentRef.current = false;
    try {
      await chatApi.setTyping(selectedChatId, false);
    } catch {
      // ignore typing sync errors
    }
  }, [selectedChatId]);

  const sendTyping = useCallback(async () => {
    if (!selectedChatId) return;

    if (!typingSentRef.current) {
      typingSentRef.current = true;
      try {
        await chatApi.setTyping(selectedChatId, true);
      } catch {
        // ignore typing sync errors
      }
    }

    if (typingIdleTimerRef.current) {
      window.clearTimeout(typingIdleTimerRef.current);
    }

    typingIdleTimerRef.current = window.setTimeout(() => {
      void stopTyping();
    }, 1500);
  }, [selectedChatId, stopTyping]);

  const markLatestRead = useCallback(async () => {
    if (!selectedChatId || !myUserId || messages.length === 0) return;

    const last = messages[messages.length - 1];
    if (!last || last.authorUserId === myUserId) return;
    if (last.id <= lastReadSentRef.current) return;

    lastReadSentRef.current = last.id;

    try {
      const state = await chatApi.markRead(selectedChatId, last.id);
      if (state.userId !== null) {
        setReadStates((prev) => ({ ...prev, [state.userId as number]: state }));
      }
      setChats((prev) => prev.map((c) => (c.id === selectedChatId ? { ...c, unreadCount: 0 } : c)));
    } catch {
      // ignore read sync errors
    }
  }, [messages, myUserId, selectedChatId]);

  const loadProfile = useCallback(async () => {
    setMeLoading(true);
    setMeError(null);
    try {
      const data = await api.get<MeResponse>("/api/me");
      setMe(data);
    } catch (e: any) {
      setMeError(e?.details?.error || e?.message || "Не удалось загрузить профиль");
    } finally {
      setMeLoading(false);
    }
  }, []);

  const loadChatSettings = useCallback(async (chatId: number) => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const data = await chatApi.getChatSettings(chatId);
      setChatSettings(data);
    } catch (e: any) {
      setSettingsError(formatChatError(e, "Не удалось загрузить настройки чата"));
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  useEffect(() => {
    if (assistantSelected) { setSelectedChatId(null); setMobileDialogOpen(true); setSelectedChatAccessError(null); }
  }, [assistantSelected]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    setSettingsScreen("root");
    setIsChatSettingsOpen(false);
    if (!selectedChatId) {
      setChatSettings(null);
      setSettingsError(null);
      return;
    }

    void loadChatSettings(selectedChatId);
  }, [loadChatSettings, selectedChatId]);

  useEffect(() => {
    if (themeMode === "dark") {
      document.body.dataset.theme = "dark";
    } else {
      delete document.body.dataset.theme;
    }
    window.localStorage.setItem("lkfr-chat-theme-v2", themeMode);
  }, [themeMode]);

  useEffect(() => {
    if (!selectedChatId) {
      setMessages([]);
      setHasMore(false);
      setStreamReadyChatId(null);
      streamCursorRef.current = 0;
      return;
    }
    setSelectedChatAccessError(null);
    setStreamReadyChatId(null);
    streamCursorRef.current = 0;
    setEditingMessageId(null);
    setReplyingToMessageId(null);
    setActiveMessageAction(null);
    setSearchOpen(false);
    setIsEmojiOpen(false);
    setIsAttachmentSheetOpen(false);
    setIsDragOver(false);
    setActiveImagePreview(null);
    setPendingFiles([]);
    shouldStickToBottomRef.current = true;
    void loadMessages(selectedChatId);
  }, [selectedChatId, loadMessages]);

  useEffect(() => {
    if (!selectedChatId || streamReadyChatId !== selectedChatId) return;
    const chatId = selectedChatId;

    return openApiEventStream(
      () => `/api/chats/${chatId}/stream?lastMessageId=${streamCursorRef.current}`,
      {
        onEvent: (event) => {
          if (event.event === "ping" || event.event === "end") return;
          if (event.event !== "chat_state") return;

          let payload: ChatStreamPayload;
          try {
            payload = JSON.parse(event.data) as ChatStreamPayload;
          } catch {
            return;
          }

          const previousCursor = streamCursorRef.current;
          const incomingMessages = Array.isArray(payload.messages) ? payload.messages : [];
          const cursorMessageId = Number(payload.cursor?.lastMessageId || 0);
          streamCursorRef.current = Math.max(
            streamCursorRef.current,
            cursorMessageId,
            getLatestMessageId(incomingMessages)
          );

          if (incomingMessages.length > 0) {
            setMessages((prev) => {
              const visibleMessageIds = new Set(prev.map((message) => message.id));
              const relevantMessages = incomingMessages.filter((message) => {
                return Number(message.id || 0) > previousCursor || visibleMessageIds.has(message.id);
              });

              return upsertMessages(prev, relevantMessages);
            });
            setMessagesError(null);

            const latestIncoming = incomingMessages.reduce<ChatMessage | null>((latest, message) => {
              if (!latest || message.id > latest.id) return message;
              return latest;
            }, null);

            if (latestIncoming) {
              setChats((prev) =>
                sortChats(
                  prev.map((chat) => {
                    if (chat.id !== chatId) return chat;

                    const shouldUpdateLast = !chat.lastMessage || latestIncoming.id >= chat.lastMessage.id;

                    return {
                      ...chat,
                      lastMessage: shouldUpdateLast ? latestIncoming : chat.lastMessage,
                      updatedAt: latestIncoming.id > previousCursor ? latestIncoming.createdAt : chat.updatedAt,
                      preview: shouldUpdateLast ? getMessagePreview(latestIncoming).slice(0, 120) : chat.preview,
                      unreadCount: 0,
                    };
                  })
                )
              );
            }
          }

          if (Array.isArray(payload.reads)) {
            const nextReadStates: Record<number, ChatReadState> = {};
            payload.reads.forEach((state) => {
              if (state.userId !== null) {
                nextReadStates[state.userId] = state;
              }
            });
            setReadStates(nextReadStates);
          }

          if (Array.isArray(payload.typing)) {
            setTypingStates(payload.typing);
          }

          if (Array.isArray(payload.pinnedMessages)) {
            setPinnedMessages(payload.pinnedMessages);
          }
        },
        onError: () => undefined,
      }
    );
  }, [selectedChatId, streamReadyChatId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setActiveMessageAction(null);
      setForwardingMessageId(null);
      setSearchOpen(false);
      setIsChatSettingsOpen(false);
      setIsCreateOpen(false);
      setIsInviteOpen(false);
      setIsEmojiOpen(false);
      setIsAttachmentSheetOpen(false);
      setActiveImagePreview(null);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    void markLatestRead();
  }, [messages, markLatestRead]);

  useEffect(() => {
    const textarea = composerRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 140)}px`;
  }, [messageDraft]);

  useEffect(() => {
    return () => {
      if (typingIdleTimerRef.current) {
        window.clearTimeout(typingIdleTimerRef.current);
      }
    };
  }, []);

  const onCreateChat = useCallback(async () => {
    const phone = createPhone.trim();
    if (!phone) return;

    setCreateBusy(true);
    try {
      const created = await chatApi.createChat({
        title: createTitle.trim() || undefined,
        isGroup: false,
        participantPhones: [phone],
      });

      setChats((prev) => [created, ...prev.filter((c) => c.id !== created.id)]);
      setSelectedChatId(created.id);
      setMobileDialogOpen(true);
      navigate(`/chats/${created.id}`);
      setCreatePhone("");
      setCreateTitle("");
      setIsCreateOpen(false);
    } catch (e: any) {
      alert(e?.details?.error || e?.message || "Не удалось создать чат");
    } finally {
      setCreateBusy(false);
    }
  }, [createPhone, createTitle, navigate]);

  const onInvite = useCallback(async () => {
    if (!selectedChatId || !invitePhone.trim()) return;

    setInviteBusy(true);
    try {
      await chatApi.inviteByPhone(selectedChatId, invitePhone.trim());
      const updated = await chatApi.getChat(selectedChatId);
      setChats((prev) => prev.map((c) => (c.id === selectedChatId ? updated : c)));
      setInvitePhone("");
      setIsInviteOpen(false);
    } catch (e: any) {
      alert(e?.details?.error || e?.message || "Не удалось пригласить пользователя");
    } finally {
      setInviteBusy(false);
    }
  }, [invitePhone, selectedChatId]);

  const applyMessageMutation = useCallback((msg: ChatMessage, isNewActivity = false) => {
    setMessages((prev) => upsertMessages(prev, [msg]));
    setChats((prev) =>
      sortChats(
        prev.map((c) =>
          c.id === msg.chatId
            ? {
                ...c,
                lastMessage: !c.lastMessage || msg.id >= c.lastMessage.id ? msg : c.lastMessage,
                updatedAt: isNewActivity ? msg.createdAt : c.updatedAt,
                preview: getMessagePreview(msg).slice(0, 120),
                unreadCount: c.id === selectedChatId ? 0 : c.unreadCount,
              }
            : c
        )
      )
    );
  }, [selectedChatId]);

  const showToast = useCallback((message: string) => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(""), 2200);
  }, []);

  const focusComposerSoon = useCallback(() => {
    window.setTimeout(() => composerRef.current?.focus(), 60);
  }, []);

  const startReply = useCallback((message: ChatMessage) => {
    if (message.isDeleted) return;
    setReplyingToMessageId(message.id);
    setEditingMessageId(null);
    setActiveMessageAction(null);
    focusComposerSoon();
  }, [focusComposerSoon]);

  const clearMessageGesture = useCallback(() => {
    if (messageGestureRef.current?.longPressTimer) {
      window.clearTimeout(messageGestureRef.current.longPressTimer);
    }
    messageGestureRef.current = null;
    setMessageSwipe(null);
  }, []);

  const beginMessageGesture = useCallback((message: ChatMessage, isMine: boolean, event: React.PointerEvent<HTMLElement>) => {
    if (message.isDeleted) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    if (messageGestureRef.current?.longPressTimer) {
      window.clearTimeout(messageGestureRef.current.longPressTimer);
    }

    const pointerId = event.pointerId;
    const longPressTimer = window.setTimeout(() => {
      if (messageGestureRef.current?.pointerId !== pointerId) return;
      setMessageSwipe(null);
      setActiveMessageAction(message);
      messageGestureRef.current = null;
    }, LONG_PRESS_DELAY);

    messageGestureRef.current = {
      id: message.id,
      isMine,
      pointerId,
      startX: event.clientX,
      startY: event.clientY,
      longPressTimer,
    };

    event.currentTarget.setPointerCapture?.(pointerId);
  }, []);

  const moveMessageGesture = useCallback((event: React.PointerEvent<HTMLElement>) => {
    const gesture = messageGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    const rawX = event.clientX - gesture.startX;
    const rawY = event.clientY - gesture.startY;
    const horizontal = Math.abs(rawX);
    const vertical = Math.abs(rawY);

    if (horizontal > 8 || vertical > 8) {
      if (gesture.longPressTimer) {
        window.clearTimeout(gesture.longPressTimer);
        gesture.longPressTimer = null;
      }
    }

    if (vertical > horizontal || horizontal < 4) return;

    const directionOffset = gesture.isMine ? Math.min(0, rawX) : Math.max(0, rawX);
    const offset = Math.max(-84, Math.min(84, directionOffset * 0.62));
    const ready = Math.abs(directionOffset) >= SWIPE_REPLY_THRESHOLD;

    if (offset !== 0) {
      event.preventDefault();
      setMessageSwipe({ id: gesture.id, offset, ready });
    }
  }, []);

  const endMessageGesture = useCallback((message: ChatMessage, event: React.PointerEvent<HTMLElement>) => {
    const gesture = messageGestureRef.current;
    const swipe = messageSwipe;
    if (!gesture || gesture.pointerId !== event.pointerId) {
      clearMessageGesture();
      return;
    }

    if (swipe?.id === message.id && swipe.ready) {
      startReply(message);
    }

    clearMessageGesture();
  }, [clearMessageGesture, messageSwipe, startReply]);

  const applyChatSettingsResult = useCallback((settings: ChatSettings, chat?: ChatItem) => {
    setChatSettings(settings);
    if (chat) {
      setChats((prev) => sortChats(prev.map((item) => (item.id === chat.id ? chat : item))));
    }
  }, []);

  const saveMuteSetting = useCallback(async (nextMuted: boolean, preset: MutePreset = mutePreset) => {
    if (!selectedChatId) return;

    setSettingsSaving(true);
    setSettingsError(null);
    try {
      const data = await chatApi.updateChatSettings(selectedChatId, {
        isMuted: nextMuted,
        mutedUntil: nextMuted ? buildMutedUntil(preset) : null,
      });
      applyChatSettingsResult(data.settings, data.chat);
      showToast(nextMuted ? "Уведомления чата отключены" : "Уведомления чата включены");
    } catch (e: any) {
      setSettingsError(e?.details?.error || e?.message || "Не удалось сохранить настройки");
    } finally {
      setSettingsSaving(false);
    }
  }, [applyChatSettingsResult, mutePreset, selectedChatId, showToast]);

  const openImportantChats = useCallback(() => {
    setListFilter("important");
    setQuery("");
    setSettingsScreen("root");
    setMobileSidebarScreen("chats");
  }, []);

  const openChatSettings = useCallback(() => {
    if (!selectedChatId) return;
    setIsChatSettingsOpen(true);
    setSettingsError(null);
    void loadChatSettings(selectedChatId);
  }, [loadChatSettings, selectedChatId]);

  const queueFiles = useCallback((files: File[] | FileList) => {
    const next = Array.from(files || []);
    if (!next.length) return;
    setPendingFiles((current) => [...current, ...next]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const removePendingFile = useCallback((index: number) => {
    setPendingFiles((current) => current.filter((_, itemIndex) => itemIndex !== index));
  }, []);

  const openAttachmentPicker = useCallback((accept = "", capture?: "environment") => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = accept;
      if (capture) {
        fileInputRef.current.setAttribute("capture", capture);
      } else {
        fileInputRef.current.removeAttribute("capture");
      }
      fileInputRef.current.click();
    }
    setIsAttachmentSheetOpen(false);
  }, []);


  const onSend = useCallback(async () => {
    if (!selectedChatId || uploadBusy) return;

    const text = messageDraft.trim();
    const hasFiles = pendingFiles.length > 0;
    if (editingMessageId && !text) return;
    if (!editingMessageId && !text && !hasFiles) return;

    setMessageDraft("");
    await stopTyping();

    try {
      if (editingMessageId) {
        const msg = await chatApi.editMessage(selectedChatId, editingMessageId, text);
        applyMessageMutation(msg, false);
        setEditingMessageId(null);
        showToast("Сообщение изменено");
        return;
      }

      setUploadBusy(hasFiles);
      const msg = hasFiles
        ? await chatApi.uploadFiles(selectedChatId, pendingFiles, {
            text: text || undefined,
            replyToMessageId: replyingToMessageId || undefined,
          })
        : await chatApi.sendMessage(selectedChatId, text, { replyToMessageId: replyingToMessageId || undefined });

      applyMessageMutation(msg, true);
      setEditingMessageId(null);
      setReplyingToMessageId(null);
      setPendingFiles([]);
    } catch (e: any) {
      setMessageDraft(text);
      alert(e?.details?.error || e?.message || "Не удалось отправить сообщение");
    } finally {
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [applyMessageMutation, editingMessageId, messageDraft, pendingFiles, replyingToMessageId, selectedChatId, showToast, stopTyping, uploadBusy]);

  const onFilePick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    queueFiles(event.target.files || []);
  }, [queueFiles]);

  const onDropFiles = useCallback((event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length) queueFiles(files);
  }, [queueFiles]);

  const startEdit = useCallback((message: ChatMessage) => {
    if (message.authorUserId !== myUserId || message.isDeleted || message.type !== "text") return;
    setEditingMessageId(message.id);
    setReplyingToMessageId(null);
    setPendingFiles([]);
    setMessageDraft(message.text || "");
    setActiveMessageAction(null);
  }, [myUserId]);

  const deleteMessage = useCallback(async (message: ChatMessage) => {
    if (!selectedChatId || !window.confirm("Удалить это сообщение?")) return;
    try {
      const deleted = await chatApi.deleteMessage(selectedChatId, message.id);
      applyMessageMutation(deleted, false);
      setActiveMessageAction(null);
    } catch (e: any) {
      alert(e?.details?.error || e?.message || "Не удалось удалить сообщение");
    }
  }, [applyMessageMutation, selectedChatId]);

  const reactToMessage = useCallback(async (message: ChatMessage, reaction: string) => {
    if (!selectedChatId) return;
    try {
      const mine = (message.reactions || []).find((item) => item.reactedByMe);
      const updated = mine?.reaction === reaction
        ? await chatApi.deleteReaction(selectedChatId, message.id)
        : await chatApi.reactToMessage(selectedChatId, message.id, reaction);
      applyMessageMutation(updated, false);
    } catch (e: any) {
      alert(e?.details?.error || e?.message || "Не удалось выполнить действие");
    } finally {
      setActiveMessageAction(null);
    }
  }, [applyMessageMutation, selectedChatId]);

  const togglePin = useCallback(async (message: ChatMessage) => {
    if (!selectedChatId) return;
    const items = message.isPinned
      ? await chatApi.unpinMessage(selectedChatId, message.id)
      : await chatApi.pinMessage(selectedChatId, message.id);
    setPinnedMessages(items);
    setMessages((prev) => prev.map((item) => item.id === message.id ? { ...item, isPinned: !message.isPinned } : item));
    setActiveMessageAction(null);
  }, [selectedChatId]);

  const toggleImportant = useCallback(async (message: ChatMessage) => {
    if (!selectedChatId) return;
    const updated = message.isImportantForMe
      ? await chatApi.unmarkMessageImportant(selectedChatId, message.id)
      : await chatApi.markMessageImportant(selectedChatId, message.id);
    applyMessageMutation(updated, false);
    setActiveMessageAction(null);
  }, [applyMessageMutation, selectedChatId]);

  const toggleSelectedChatPinned = useCallback(async () => {
    if (!selectedChatId || !selectedChat) return;
    try {
      const nextPinned = !selectedChat.isPinned;
      const data = await chatApi.updateChatSettings(selectedChatId, { isPinned: nextPinned });
      applyChatSettingsResult(data.settings, data.chat || { ...selectedChat, isPinned: nextPinned, pinnedAt: nextPinned ? new Date().toISOString() : null });
      showToast(nextPinned ? "Чат закреплен" : "Чат откреплен");
    } catch (e: any) {
      alert(e?.details?.error || e?.message || "Не удалось обновить чат");
    }
  }, [applyChatSettingsResult, selectedChat, selectedChatId, showToast]);

  const toggleSelectedChatImportant = useCallback(async () => {
    if (!selectedChatId || !selectedChat) return;
    try {
      const nextImportant = !selectedChat.isImportant;
      const data = await chatApi.updateChatSettings(selectedChatId, { isImportant: nextImportant });
      applyChatSettingsResult(data.settings, data.chat || { ...selectedChat, isImportant: nextImportant, importantAt: nextImportant ? new Date().toISOString() : null });
      showToast(nextImportant ? "Чат отмечен важным" : "Чат убран из важных");
    } catch (e: any) {
      alert(e?.details?.error || e?.message || "Не удалось обновить чат");
    }
  }, [applyChatSettingsResult, selectedChat, selectedChatId, showToast]);

  const forwardToChat = useCallback(async (targetChatId: number) => {
    if (!forwardingMessageId) return;
    try {
      const msg = await chatApi.forwardMessage(targetChatId, forwardingMessageId);
      if (targetChatId === selectedChatId) setMessages((prev) => upsertMessages(prev, [msg]));
      setChats((prev) => sortChats(prev.map((chat) => chat.id === targetChatId ? { ...chat, lastMessage: msg, updatedAt: msg.createdAt, preview: getMessagePreview(msg) } : chat)));
      setForwardingMessageId(null);
      showToast("Сообщение переслано");
    } catch (e: any) {
      alert(e?.details?.error || e?.message || "Не удалось переслать сообщение");
    }
  }, [forwardingMessageId, selectedChatId, showToast]);

  const searchMessages = useCallback(async () => {
    if (!selectedChatId) return;
    const items = await chatApi.searchMessages(selectedChatId, { q: searchQuery.trim(), limit: 40 });
    setSearchResults(items);
  }, [searchQuery, selectedChatId]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const panel = messagesPanelRef.current;
    if (!panel) return;
    panel.scrollTo({ top: panel.scrollHeight, behavior });
    shouldStickToBottomRef.current = true;
    setShowJumpButton(false);
  }, []);

  const syncJumpButton = useCallback(() => {
    const panel = messagesPanelRef.current;
    if (!panel) return;
    const distance = panel.scrollHeight - panel.scrollTop - panel.clientHeight;
    shouldStickToBottomRef.current = distance < 120;
    setShowJumpButton(distance > 240);
  }, []);

  useEffect(() => {
    if (shouldStickToBottomRef.current) {
      window.setTimeout(() => scrollToBottom("auto"), 20);
    }
  }, [messages.length, scrollToBottom]);

  const toggleThemeMode = useCallback(() => {
    setThemeMode((current) => current === "dark" ? "light" : "dark");
  }, []);

  const selectedOtherParticipants = useMemo(
    () => selectedChat?.participants.filter((p) => !p.isMe) ?? [],
    [selectedChat]
  );

  const myChatPreferences = useMemo<ChatPreferences>(() => {
    return chatSettings?.preferences
      ?? selectedChat?.participants.find((participant) => participant.isMe)?.preferences
      ?? {};
  }, [chatSettings, selectedChat]);

  const mediaCounters = useMemo(() => ({
    media: chatSettings?.mediaCounters?.media ?? selectedChat?.mediaCounters?.media ?? 0,
    files: chatSettings?.mediaCounters?.files ?? selectedChat?.mediaCounters?.files ?? 0,
    links: chatSettings?.mediaCounters?.links ?? selectedChat?.mediaCounters?.links ?? 0,
  }), [chatSettings, selectedChat]);
  const importantChatsCount = chats.filter((chat) => chat.isImportant).length;
  const profileLabel = meLoading ? "Загрузка..." : (me?.name?.trim() || me?.phone || "Профиль");
  const profileMeta = meError || me?.phone || me?.email || "Данные профиля";
  const profileInitials = getInitials(me?.name?.trim() || me?.phone || "ИИ");
  const currentChatMuted = Boolean(myChatPreferences.isMuted);
  const muteStatus = formatMuteStatus(currentChatMuted, myChatPreferences.mutedUntil);
  const mediaTotal = Number(mediaCounters.media || 0) + Number(mediaCounters.files || 0) + Number(mediaCounters.links || 0);

  const typingLabel = useMemo(() => {
    if (typingUserIds.length === 0) return "";

    const labels = selectedOtherParticipants
      .filter((p) => p.userId !== null && typingUserIds.includes(p.userId))
      .map((p) => p.displayName || p.phone || "Участник")
      .slice(0, 2);

    if (!labels.length) return "Печатает...";
    if (labels.length === 1) return `${labels[0]} печатает...`;
    return `${labels[0]} и ${labels[1]} печатают...`;
  }, [selectedOtherParticipants, typingUserIds]);

  const isReadBySomeone = useCallback(
    (messageId: number): boolean => {
      if (!myUserId) return false;

      return Object.values(readStates).some((state) => {
        if (!state.userId || state.userId === myUserId || !state.lastReadMessageId) return false;
        return state.lastReadMessageId >= messageId;
      });
    },
    [readStates, myUserId]
  );

  const jumpToMessage = useCallback((messageId: number) => {
    setSearchOpen(false);
    window.setTimeout(() => {
      document.getElementById(`lkfr-chat-message-${messageId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 20);
  }, []);

  return (
    <div className={`chats-page ${editingMessage ? "is-editing-message" : ""}`} data-chat-theme={themeMode}>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      <div className={`chats-shell ${mobileDialogOpen && (selectedChat || assistantSelected) ? "mobile-dialog-open" : ""}`}>
        <aside className="chats-sidebar">
          <div className="chats-sidebar-head">
            <button className="chats-round-action chats-menu-trigger" type="button" onClick={() => setMenuOpen(true)} aria-label="Открыть меню">
              <Icon name="menu" />
            </button>
            <div className="chats-sidebar-title">Чаты</div>
            <div className="chats-sidebar-actions">
              <button className="chats-round-action" type="button" onClick={toggleThemeMode} aria-label={themeMode === "dark" ? "Светлая тема" : "Темная тема"}>
                <Icon name={themeMode === "dark" ? "sun" : "moon"} />
              </button>
              <button className="chats-round-action" type="button" onClick={() => setIsCreateOpen(true)} aria-label="Новый чат">
                <Icon name="plus" />
              </button>
            </div>
          </div>

          <div className={`chats-sidebar-body ${mobileSidebarScreen === "settings" ? "show-settings" : "show-chats"}`}>
            <div className="chat-list-screen">
              <div className="chats-search-box">
                <Icon name="search" />
                <input
                  className="chats-input chats-search"
                  placeholder="Поиск"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query.trim() && (
                  <button className="chats-search-cancel" type="button" onClick={() => setQuery("")} aria-label="Очистить поиск">
                    <Icon name="close" />
                  </button>
                )}
              </div>

              <div className="chats-filter-row">
                {[
                  { key: "all", label: "Все", count: chats.length },
                  { key: "unread", label: "Непрочитанные", count: chats.filter((chat) => chat.unreadCount > 0).length },
                  { key: "important", label: "Важные", count: chats.filter((chat) => chat.isImportant).length },
                ].map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    className={`chat-filter-chip ${listFilter === filter.key ? "active" : ""}`}
                    onClick={() => setListFilter(filter.key as "all" | "unread" | "important")}
                  >
                    {filter.label}
                    <span>{filter.count}</span>
                  </button>
                ))}
              </div>

              {query.trim() && (
                <div className="mobile-search-screen">
                  <div className="mobile-search-tabs">
                    <button className="active" type="button">Чаты</button>
                    <button type="button">Медиа</button>
                    <button type="button">Ссылки</button>
                    <button type="button">Файлы</button>
                    <button type="button">Музыка</button>
                  </div>
                  <div className="mobile-search-count">Найдено {filteredChats.length} чатов</div>
                </div>
              )}

              <div className="chats-list">
                {listFilter === "all" && (!query.trim() || /ии|помощ|ассист|ai/i.test(query)) && <button type="button" className={`chat-row ${assistantSelected ? "active" : ""}`}
                  onClick={() => { setSelectedChatId(null); setMobileDialogOpen(true); navigate("/chats/assistant"); }}>
                  <span className="chat-avatar-wrap"><span className="chat-avatar">ИИ</span></span>
                  <div className="chat-row-copy"><div className="chat-row-title">ИИ-помощник</div><div className="chat-row-preview">Ваши салоны и база знаний</div></div>
                </button>}
                {loadingChats && filteredChats.length === 0 && <ChatListSkeleton />}
                {chatsError && filteredChats.length === 0 && <div className="chats-empty chats-error">{chatsError}</div>}
                {chatsError && filteredChats.length > 0 && (
                  <div className="chat-list-status">
                    <span>{chatsError}</span>
                    <button type="button" onClick={() => void loadChats()}>Повторить</button>
                  </div>
                )}
                {!loadingChats && !chatsError && filteredChats.length === 0 && (
                  <div className="chats-empty">Пока нет чатов</div>
                )}

                {filteredChats.map((chat) => {
                  const chatName = buildChatName(chat);
                  const online = isChatOnline(chat);

                  return (
                    <button
                      key={chat.id}
                      type="button"
                      className={`chat-row ${selectedChatId === chat.id ? "active" : ""} ${chat.isPinned ? "pinned" : ""} ${chat.isImportant ? "important" : ""} ${chat.unreadCount > 0 ? "unread" : ""}`}
                      onClick={() => {
                        setSelectedChatId(chat.id);
                        setMobileDialogOpen(true);
                        setSelectedChatAccessError(null);
                        navigate(`/chats/${chat.id}`);
                      }}
                    >
                      <span className={`chat-avatar-wrap ${online ? "online" : ""}`}>
                        <span className="chat-avatar">{getInitials(chatName)}</span>
                      </span>
                      <div className="chat-row-copy">
                        <div className="chat-row-top">
                          <div className="chat-row-title">{chatName}</div>
                          <div className="chat-row-time">
                            {chat.isPinned && <Icon name="pin" />}
                            {chat.isImportant && <Icon name="star" />}
                            {formatListTime(chat.lastMessage ? chat.lastMessage.createdAt : chat.updatedAt)}
                          </div>
                        </div>
                        <div className="chat-row-preview">{getMessagePreview(chat.lastMessage)}</div>
                        {chat.unreadCount > 0 && <span className="chat-unread-badge">{chat.unreadCount}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mobile-settings-screen">
              {settingsScreen === "root" && (
                <>
                  <div className="mobile-settings-title">
                    <span>Настройки</span>
                    <button type="button" onClick={() => navigate("/profile")}>Изм.</button>
                  </div>
                  <button className="mobile-settings-profile" type="button" onClick={() => navigate("/profile")}>
                    {me?.avatarUrl ? (
                      <img className="chat-avatar" src={me.avatarUrl} alt={profileLabel} />
                    ) : (
                      <span className="chat-avatar">{profileInitials}</span>
                    )}
                    <span>
                      <strong>{profileLabel}</strong>
                      <em className={meError ? "error" : ""}>{profileMeta}</em>
                    </span>
                    <Icon name="down" />
                  </button>

                  <div className="mobile-settings-group">
                    <button className="mobile-settings-row" type="button" onClick={openImportantChats}>
                      <span className="settings-icon blue"><Icon name="star" /></span>
                      <strong>Важные</strong>
                      <b>{importantChatsCount}</b>
                      <Icon name="down" />
                    </button>

                    <button className="mobile-settings-row" type="button" onClick={() => setSettingsScreen("appearance")}>
                      <span className="settings-icon blue"><Icon name="image" /></span>
                      <strong>Оформление</strong>
                      <em>{themeMode === "dark" ? "Тёмная" : "Светлая"}</em>
                      <Icon name="down" />
                    </button>
                  </div>
                </>
              )}

              {settingsScreen === "appearance" && (
                <>
                  <div className="mobile-settings-title sub">
                    <button className="mobile-settings-back" type="button" onClick={() => setSettingsScreen("root")} aria-label="Назад">
                      <Icon name="back" />
                    </button>
                    <span>Оформление</span>
                  </div>

                  <div className="mobile-settings-panel">
                    <div className="mobile-settings-section-title">Тема</div>
                    <div className="mobile-settings-choice-grid two">
                      <button className={`mobile-settings-choice ${themeMode === "light" ? "active" : ""}`} type="button" onClick={() => setThemeMode("light")}>
                        Светлая
                      </button>
                      <button className={`mobile-settings-choice ${themeMode === "dark" ? "active" : ""}`} type="button" onClick={() => setThemeMode("dark")}>
                        Тёмная
                      </button>
                    </div>
                    <div className="mobile-settings-note">Выбор темы сохраняется на этом устройстве.</div>
                  </div>
                </>
              )}

            </div>
          </div>

          <nav className="mobile-tabbar" aria-label="Разделы чата">
            <button type="button" onClick={() => setMobileSidebarScreen("chats")} className={mobileSidebarScreen === "chats" ? "active" : ""}>
              <Icon name="chat" />
              <span>Чаты</span>
              {chats.some((chat) => chat.unreadCount > 0) && <b>{chats.reduce((sum, chat) => sum + chat.unreadCount, 0)}</b>}
            </button>
            <button type="button" onClick={() => { setSettingsScreen("root"); setMobileSidebarScreen("settings"); }} className={mobileSidebarScreen === "settings" ? "active" : ""}>
              <Icon name="gear" />
              <span>Настройки</span>
            </button>
          </nav>
        </aside>

        <main className="chats-main">
          {assistantSelected && <AssistantConversation key={me?.id ?? "anonymous"} userId={me?.id ?? null} onBack={() => setMobileDialogOpen(false)} />}
          {!assistantSelected && !selectedChat && (
            <div className={`chats-empty ${selectedChatAccessError ? "chats-error" : ""}`}>
              {selectedChatAccessError || "Выберите чат слева"}
            </div>
          )}

          {!assistantSelected && selectedChat && (
            <>
              <header className="chats-header">
                <div className="chats-header-main">
                  <button className="chats-icon chat-mobile-back" type="button" onClick={() => setMobileDialogOpen(false)} aria-label="К списку чатов">
                    <Icon name="back" />
                  </button>
                  <span className={`chat-avatar-wrap large ${isChatOnline(selectedChat) ? "online" : ""}`}>
                    <span className="chat-avatar large">{getInitials(buildChatName(selectedChat))}</span>
                  </span>
                  <div>
                    <div className="chats-header-name">{buildChatName(selectedChat)}</div>
                    <div className="chats-header-meta">
                      {typingLabel || (isChatOnline(selectedChat) ? "в сети" : selectedOtherParticipants.map((p) => p.phone || "").join(" • ") || "Личный диалог")}
                    </div>
                  </div>
                </div>

                <div className="chats-header-actions">
                  <button className="chats-icon" type="button" onClick={() => setSearchOpen(true)} aria-label="Поиск по сообщениям"><Icon name="search" /></button>
                  <button className={`chats-icon ${isChatSettingsOpen ? "active" : ""}`} type="button" onClick={openChatSettings} aria-label="Настройки чата"><Icon name="gear" /></button>
                  <button className={`chats-icon ${selectedChat.isPinned ? "active" : ""}`} type="button" onClick={() => void toggleSelectedChatPinned()} aria-label={selectedChat.isPinned ? "Открепить чат" : "Закрепить чат"}><Icon name="pin" /></button>
                  <button className={`chats-icon ${selectedChat.isImportant ? "active" : ""}`} type="button" onClick={() => void toggleSelectedChatImportant()} aria-label={selectedChat.isImportant ? "Убрать из важных" : "Отметить важным"}><Icon name="star" /></button>
                  <button className="chats-icon" type="button" onClick={() => setIsInviteOpen(true)} aria-label="Пригласить участника"><Icon name="plus" /></button>
                </div>
              </header>

              {latestPinnedMessage?.message && (
                <button className="pinned-message-bar" type="button" onClick={() => latestPinnedMessage.message && jumpToMessage(latestPinnedMessage.message.id)}>
                  <Icon name="pin" />
                  <strong>Закрепленные сообщения{pinnedMessages.length > 1 ? ` · ${pinnedMessages.length}` : ""}</strong>
                  <em>{getMessagePreview(latestPinnedMessage.message)}</em>
                </button>
              )}

              <section
                className={`messages-panel ${isDragOver ? "drag-over" : ""}`}
                ref={messagesPanelRef}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={(event) => {
                  if (event.currentTarget === event.target) setIsDragOver(false);
                }}
                onDrop={(event) => void onDropFiles(event)}
                onScroll={syncJumpButton}
              >
                {messagesLoading && messages.length === 0 && <MessageListSkeleton />}
                {messagesError && messages.length === 0 && <div className="chats-empty chats-error">{messagesError}</div>}
                {messagesError && messages.length > 0 && (
                  <div className="chat-list-status messages-status">
                    <span>{messagesError}</span>
                    <button type="button" onClick={() => selectedChatId && void loadMessages(selectedChatId)}>Повторить</button>
                  </div>
                )}

                {!messagesLoading && hasMore && messages.length > 0 && (
                  <div className="messages-loadmore-wrap">
                    <button className="messages-loadmore" onClick={() => void loadMessages(selectedChat.id, messages[0]?.id)}>
                      Загрузить предыдущие
                    </button>
                  </div>
                )}

                {!messagesLoading && messages.length === 0 && <div className="chats-empty">Сообщений пока нет</div>}

                {messages.map((msg, index) => {
                  const mine = myUserId !== null && myUserId === msg.authorUserId;
                  const previous = messages[index - 1];
                  const next = messages[index + 1];
                  const showDate = !previous || formatDateLabel(previous.createdAt) !== formatDateLabel(msg.createdAt);
                  const samePreviousGroup = Boolean(
                    previous &&
                    previous.authorUserId === msg.authorUserId &&
                    formatDateLabel(previous.createdAt) === formatDateLabel(msg.createdAt)
                  );
                  const sameNextGroup = Boolean(
                    next &&
                    next.authorUserId === msg.authorUserId &&
                    formatDateLabel(next.createdAt) === formatDateLabel(msg.createdAt)
                  );
                  const firstLink = (msg.links || [])[0];
                  const swipeForMessage = messageSwipe?.id === msg.id ? messageSwipe : null;
                  const swipeStyle = swipeForMessage
                    ? ({ "--swipe-x": `${swipeForMessage.offset}px` } as React.CSSProperties)
                    : undefined;
                  return (
                    <React.Fragment key={msg.id}>
                      {showDate && <div className="messages-date-divider"><span>{formatDateLabel(msg.createdAt)}</span></div>}
                      <article
                        id={`lkfr-chat-message-${msg.id}`}
                        className={`message-bubble ${mine ? "mine" : "other"} ${samePreviousGroup ? "grouped-prev" : "starts-group"} ${sameNextGroup ? "grouped-next" : "ends-group"} ${msg.isPinned ? "pinned" : ""} ${msg.isImportantForMe ? "important" : ""} ${swipeForMessage ? "is-swiping" : ""} ${swipeForMessage?.ready ? "swipe-ready" : ""}`}
                        style={swipeStyle}
                        onDoubleClick={() => startReply(msg)}
                        onContextMenu={(event) => {
                          event.preventDefault();
                          if (!msg.isDeleted) setActiveMessageAction(msg);
                        }}
                        onPointerDown={(event) => beginMessageGesture(msg, mine, event)}
                        onPointerMove={moveMessageGesture}
                        onPointerUp={(event) => endMessageGesture(msg, event)}
                        onPointerCancel={clearMessageGesture}
                      >
                        <span className="message-swipe-reply" aria-hidden="true"><Icon name="reply" /></span>
                        {msg.replyTo && (
                          <button className="message-reply-preview" type="button">
                            <strong>Ответ</strong>
                            <span>{msg.replyTo.isDeleted ? "Сообщение удалено" : msg.replyTo.text || "Вложение"}</span>
                          </button>
                        )}
                        {msg.forwardedFrom && <div className="message-forwarded">↪ Переслано</div>}
                        {Boolean(msg.attachments?.length) && (
                          <div className="attachment-stack">
                            {(msg.attachments || []).map((attachment) => (
                              <AttachmentView key={attachment.id} attachment={attachment} onOpenImage={setActiveImagePreview} />
                            ))}
                          </div>
                        )}
                        {msg.isDeleted ? (
                          <div className="message-text deleted">Сообщение удалено</div>
                        ) : msg.text ? (
                          <MessageText text={msg.text} />
                        ) : null}
                        {!msg.isDeleted && firstLink && (
                          <a className="link-preview-card" href={firstLink} target="_blank" rel="noreferrer">
                            {firstLink}
                          </a>
                        )}
                        {msg.reactions?.length ? (
                          <div className="message-reactions">
                            {msg.reactions.map((reaction) => (
                              <button key={reaction.reaction} type="button" className={`message-reaction ${reaction.reactedByMe ? "mine" : ""}`} onClick={() => void reactToMessage(msg, reaction.reaction)}>
                                {reaction.reaction} <strong>{reaction.count}</strong>
                              </button>
                            ))}
                          </div>
                        ) : null}
                        <div className="message-meta">
                          <span>{formatTime(msg.createdAt)}</span>
                          {msg.isPinned && <span>⌖</span>}
                          {msg.isImportantForMe && <span>★</span>}
                          {mine && <span className="read-indicator">{isReadBySomeone(msg.id) ? "✓✓" : "✓"}</span>}
                          {!msg.isDeleted && (
                            <button className="message-more" type="button" onClick={() => setActiveMessageAction(msg)} aria-label="Действия сообщения"><Icon name="more" /></button>
                          )}
                        </div>
                      </article>
                    </React.Fragment>
                  );
                })}
              </section>

              {showJumpButton && (
                <button className="jump-to-bottom" type="button" onClick={() => scrollToBottom()} aria-label="К новым сообщениям">
                  <Icon name="down" />
                </button>
              )}

              <footer className="composer">
                {replyingToMessage && !editingMessage && (
                  <div className="composer-preview">
                    <strong>Ответ</strong>
                    <span>{getMessagePreview(replyingToMessage)}</span>
                    <button type="button" onClick={() => setReplyingToMessageId(null)} aria-label="Отменить ответ"><Icon name="close" /></button>
                  </div>
                )}
                {editingMessage && (
                  <div className="composer-preview">
                    <strong>Редактирование</strong>
                    <span>{editingMessage.text}</span>
                    <button type="button" onClick={() => { setEditingMessageId(null); setMessageDraft(""); }} aria-label="Отменить редактирование"><Icon name="close" /></button>
                  </div>
                )}
                {!editingMessage && pendingFiles.length > 0 && (
                  <div className="composer-attachments">
                    {pendingFiles.map((file, index) => (
                      <div className="composer-attachment-chip" key={`${file.name}-${file.size}-${index}`}>
                        <span>
                          <strong>{file.name}</strong>
                          <em>{formatFileSize(file.size)}</em>
                        </span>
                        <button type="button" onClick={() => removePendingFile(index)} aria-label="Убрать вложение">
                          <Icon name="close" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input ref={fileInputRef} className="composer-file-input" type="file" multiple onChange={onFilePick} />
                <button className="chats-icon" type="button" onClick={() => setIsAttachmentSheetOpen(true)} disabled={uploadBusy || Boolean(editingMessageId)} aria-label="Прикрепить файл"><Icon name="attach" /></button>
                <button className="chats-icon" type="button" onClick={() => setIsEmojiOpen((current) => !current)} disabled={uploadBusy} aria-label="Emoji"><Icon name="smile" /></button>
                <textarea
                  ref={composerRef}
                  className="composer-input"
                  placeholder="Напишите сообщение"
                  rows={1}
                  value={messageDraft}
                  onChange={(e) => {
                    setMessageDraft(e.target.value);
                    void sendTyping();
                  }}
                  onBlur={() => {
                    void stopTyping();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void onSend();
                    }
                  }}
                />
                <button
                  className="chats-primary composer-send"
                  type="button"
                  onClick={() => void onSend()}
                  disabled={uploadBusy || (editingMessageId ? !messageDraft.trim() : !messageDraft.trim() && pendingFiles.length === 0)}
                  aria-label={editingMessageId ? "Сохранить сообщение" : "Отправить сообщение"}
                >
                  {editingMessageId ? "✓" : uploadBusy ? "..." : <Icon name="send" />}
                </button>
                {isEmojiOpen && (
                  <div className="emoji-panel">
                    {["👍", "❤️", "🔥", "👏", "😁", "😮", "😢", "🙏"].map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        className="emoji-choice"
                        onClick={() => setMessageDraft((current) => current + emoji)}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </footer>
            </>
          )}
        </main>
      </div>

      {isChatSettingsOpen && selectedChat && (
        <div className="chat-sheet chat-settings-sheet" role="dialog" aria-modal="true" onClick={() => setIsChatSettingsOpen(false)}>
          <div className="chat-settings-card" onClick={(event) => event.stopPropagation()}>
            <header className="chat-settings-head">
              <span className={`chat-avatar-wrap large ${isChatOnline(selectedChat) ? "online" : ""}`}>
                <span className="chat-avatar large">{getInitials(buildChatName(selectedChat))}</span>
              </span>
              <span>
                <strong>{buildChatName(selectedChat)}</strong>
                <em>{isChatOnline(selectedChat) ? "в сети" : selectedOtherParticipants.map((p) => p.phone || "").join(" • ") || "Личный диалог"}</em>
              </span>
              <button type="button" onClick={() => setIsChatSettingsOpen(false)} aria-label="Закрыть настройки чата">
                <Icon name="close" />
              </button>
            </header>

            <div className="chat-settings-section">
              <div className="chat-settings-toggle-row">
                <span>
                  <strong>Отключить уведомления</strong>
                  <em>{muteStatus}</em>
                </span>
                <button
                  className={`settings-switch ${currentChatMuted ? "on" : ""}`}
                  type="button"
                  disabled={settingsSaving}
                  onClick={() => void saveMuteSetting(!currentChatMuted)}
                  aria-label={currentChatMuted ? "Включить уведомления" : "Отключить уведомления"}
                >
                  <i />
                </button>
              </div>

              <div className="chat-settings-section-title">Срок отключения</div>
              <div className="chat-settings-choice-grid">
                {[
                  { key: "1h" as MutePreset, label: "1 час" },
                  { key: "8h" as MutePreset, label: "8 часов" },
                  { key: "tomorrow" as MutePreset, label: "До завтра" },
                  { key: "forever" as MutePreset, label: "Навсегда" },
                ].map((option) => (
                  <button
                    key={option.key}
                    className={`chat-settings-choice ${mutePreset === option.key ? "active" : ""}`}
                    type="button"
                    disabled={settingsSaving}
                    onClick={() => {
                      setMutePreset(option.key);
                      void saveMuteSetting(true, option.key);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="chat-settings-section">
              <div className="chat-settings-section-title">Медиа и файлы · {mediaTotal}</div>
              <div className="chat-settings-stats">
                <span>
                  <strong>{mediaCountLabel(mediaCounters.media)}</strong>
                  <em>Медиа</em>
                </span>
                <span>
                  <strong>{mediaCountLabel(mediaCounters.files)}</strong>
                  <em>Файлы</em>
                </span>
                <span>
                  <strong>{mediaCountLabel(mediaCounters.links)}</strong>
                  <em>Ссылки</em>
                </span>
              </div>
            </div>

            <div className="chat-settings-section">
              <button className="chat-settings-row" type="button" onClick={() => void toggleSelectedChatPinned()}>
                <span className="settings-icon blue"><Icon name="pin" /></span>
                <strong>Закрепить чат</strong>
                <span className={`settings-switch ${selectedChat.isPinned ? "on" : ""}`}><i /></span>
              </button>
              <button className="chat-settings-row" type="button" onClick={() => void toggleSelectedChatImportant()}>
                <span className="settings-icon purple"><Icon name="star" /></span>
                <strong>Важный чат</strong>
                <span className={`settings-switch ${selectedChat.isImportant ? "on" : ""}`}><i /></span>
              </button>
            </div>

            {settingsLoading && <div className="chat-settings-note">Загружаем настройки чата...</div>}
            {settingsSaving && <div className="chat-settings-note">Сохраняем...</div>}
            {settingsError && <div className="chat-settings-note error">{settingsError}</div>}

            <button className="chat-settings-refresh" type="button" onClick={() => selectedChatId && loadChatSettings(selectedChatId)}>
              Обновить
            </button>
          </div>
        </div>
      )}

      {activeMessageAction && (
        <div className="chat-sheet message-action-sheet" role="dialog" aria-modal="true" onClick={() => setActiveMessageAction(null)}>
          <div className={`message-action-preview message-bubble ${activeMessageAction.authorUserId === myUserId ? "mine" : "other"}`} onClick={(event) => event.stopPropagation()}>
            <div className="message-text">{getMessagePreview(activeMessageAction)}</div>
            <div className="message-meta"><span>{formatTime(activeMessageAction.createdAt)}</span></div>
          </div>
          <div className="chat-sheet-card" onClick={(event) => event.stopPropagation()}>
            <div className="reaction-row">
              {["👍", "❤️", "🔥", "👏", "😁", "🙏"].map((reaction) => (
                <button key={reaction} type="button" onClick={() => void reactToMessage(activeMessageAction, reaction)}>{reaction}</button>
              ))}
              <button type="button" onClick={() => setActiveMessageAction(null)} aria-label="Закрыть"><Icon name="close" /></button>
            </div>
            <button type="button" onClick={() => startReply(activeMessageAction)}><span>Ответить</span><Icon name="reply" /></button>
            <button type="button" onClick={() => { void navigator.clipboard?.writeText(activeMessageAction.text || ""); setActiveMessageAction(null); showToast("Сообщение скопировано"); }}><span>Копировать</span><Icon name="copy" /></button>
            {activeMessageAction.authorUserId === myUserId && activeMessageAction.type === "text" && !activeMessageAction.isDeleted && (
              <button type="button" onClick={() => startEdit(activeMessageAction)}><span>Редактировать</span><Icon name="edit" /></button>
            )}
            {activeMessageAction.authorUserId === myUserId && !activeMessageAction.isDeleted && (
              <button className="danger" type="button" onClick={() => void deleteMessage(activeMessageAction)}><span>Удалить</span><Icon name="trash" /></button>
            )}
            <button type="button" onClick={() => { setForwardingMessageId(activeMessageAction.id); setActiveMessageAction(null); }}><span>Переслать</span><Icon name="forward" /></button>
            <button type="button" onClick={() => void togglePin(activeMessageAction)}><span>{activeMessageAction.isPinned ? "Открепить" : "Закрепить"}</span><Icon name="pin" /></button>
            <button type="button" onClick={() => void toggleImportant(activeMessageAction)}><span>{activeMessageAction.isImportantForMe ? "Убрать из важных" : "Отметить как важное"}</span><Icon name="star" /></button>
            <button type="button" onClick={() => setActiveMessageAction(null)}><span>Выбрать</span><Icon name="select" /></button>
          </div>
        </div>
      )}

      {isAttachmentSheetOpen && (
        <div className="chat-sheet attachment-action-sheet" role="dialog" aria-modal="true" onClick={() => setIsAttachmentSheetOpen(false)}>
          <div className="attachment-sheet-grid" onClick={(event) => event.stopPropagation()}>
            {[
              { icon: "image" as IconName, label: "Фото", color: "blue", accept: "image/*" },
              { icon: "camera" as IconName, label: "Камера", color: "purple", accept: "image/*", capture: "environment" as const },
              { icon: "file" as IconName, label: "Файл", color: "orange", accept: "" },
              { icon: "poll" as IconName, label: "Опрос", color: "green", unavailable: true },
              { icon: "file" as IconName, label: "Документ", color: "blue", accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf" },
            ].map((item) => {
              const unavailable = "unavailable" in item && item.unavailable;
              const accept = "accept" in item ? item.accept : "";
              const capture = "capture" in item ? item.capture : undefined;
              return (
                <button
                  key={item.label}
                  type="button"
                  className={unavailable ? "is-disabled" : ""}
                  disabled={unavailable}
                  title={unavailable ? "Пока недоступно" : item.label}
                  onClick={() => openAttachmentPicker(accept || "", capture)}
                >
                  <span className={`attachment-sheet-icon ${item.color}`}><Icon name={item.icon} /></span>
                  <strong>{item.label}{unavailable ? " · скоро" : ""}</strong>
                </button>
              );
            })}
          </div>
          <button className="attachment-sheet-cancel" type="button" onClick={() => setIsAttachmentSheetOpen(false)}>Отмена</button>
        </div>
      )}

      {forwardingMessageId && (
        <div className="chat-sheet" role="dialog" aria-modal="true">
          <div className="chat-sheet-card">
            <h3>Переслать</h3>
            {sortChats(chats).map((chat) => (
              <button key={chat.id} type="button" className="forward-chat-row" onClick={() => void forwardToChat(chat.id)}>
                <span className="chat-avatar small">{getInitials(buildChatName(chat))}</span>
                {buildChatName(chat)}
              </button>
            ))}
            <button type="button" onClick={() => setForwardingMessageId(null)}>Отмена</button>
          </div>
        </div>
      )}

      {searchOpen && (
        <div className="chat-sheet" role="dialog" aria-modal="true">
          <div className="chat-sheet-card">
            <h3>Поиск по сообщениям</h3>
            <input className="chats-input" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Найти сообщение" />
            <button className="chats-primary" type="button" onClick={() => void searchMessages()}>Найти</button>
            {searchResults.map((message) => (
              <button key={message.id} type="button" className="search-result-row" onClick={() => jumpToMessage(message.id)}>
                {getMessagePreview(message)}
              </button>
            ))}
            <button type="button" onClick={() => setSearchOpen(false)}>Закрыть</button>
          </div>
        </div>
      )}

      {isCreateOpen && (
        <div className="chat-sheet" role="dialog" aria-modal="true">
          <div className="chat-sheet-card">
            <h3>Новый чат</h3>
            <input
              className="chats-input"
              placeholder="Телефон"
              value={createPhone}
              onChange={(e) => setCreatePhone(e.target.value)}
            />
            <input
              className="chats-input"
              placeholder="Название (опционально)"
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
            />
            <button className="chats-primary" type="button" onClick={onCreateChat} disabled={createBusy || !createPhone.trim()}>
              {createBusy ? "Создание..." : "Создать"}
            </button>
            <button type="button" onClick={() => setIsCreateOpen(false)}>Отмена</button>
          </div>
        </div>
      )}

      {isInviteOpen && selectedChat && (
        <div className="chat-sheet" role="dialog" aria-modal="true">
          <div className="chat-sheet-card">
            <h3>Пригласить участника</h3>
            <input
              className="chats-input"
              placeholder="Телефон"
              value={invitePhone}
              onChange={(e) => setInvitePhone(e.target.value)}
            />
            <button className="chats-primary" type="button" onClick={onInvite} disabled={inviteBusy || !invitePhone.trim()}>
              {inviteBusy ? "..." : "Пригласить"}
            </button>
            <button type="button" onClick={() => setIsInviteOpen(false)}>Отмена</button>
          </div>
        </div>
      )}

      {editingMessage && (
        <div className="mobile-edit-screen" role="dialog" aria-modal="true">
          <header>
            <button type="button" onClick={() => { setEditingMessageId(null); setMessageDraft(""); }} aria-label="Закрыть">
              <Icon name="close" />
            </button>
            <strong>Редактировать</strong>
            <span />
          </header>
          <div className="mobile-edit-body">
            <p>{editingMessage.text}</p>
            <div className="mobile-edit-input">
              <Icon name="attach" />
              <textarea
                value={messageDraft}
                onChange={(event) => setMessageDraft(event.target.value)}
                rows={3}
                autoFocus
              />
              <button type="button" onClick={() => void onSend()} disabled={!messageDraft.trim()}>
                <Icon name="select" />
              </button>
            </div>
          </div>

        </div>
      )}

      {activeImagePreview && (
        <div className="image-modal telegram-media-viewer" role="dialog" aria-modal="true" onClick={() => setActiveImagePreview(null)}>
          <header onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setActiveImagePreview(null)} aria-label="Закрыть"><Icon name="back" /></button>
            <strong>{activeImageIndex >= 0 ? `${activeImageIndex + 1} из ${Math.max(1, imageAttachments.length)}` : "1 из 1"}</strong>
            <span>
              <button type="button" aria-label="Скопировать ссылку" onClick={() => { void navigator.clipboard?.writeText(activeImagePreview.url).then(() => showToast("Ссылка скопирована"), () => showToast("Не удалось скопировать ссылку")); }}><Icon name="copy" /></button>
              <a href={activeImagePreview.url} target="_blank" rel="noopener noreferrer" aria-label="Открыть изображение"><Icon name="more" /></a>
            </span>
          </header>
          <div className="image-modal-card" onClick={(event) => event.stopPropagation()}>
            <img src={activeImagePreview.url} alt={activeImagePreview.title} />
          </div>
          <footer onClick={(event) => event.stopPropagation()}>
            <div className="media-thumbnails">
              {(imageAttachments.length ? imageAttachments : [{ id: 0, url: activeImagePreview.url, title: activeImagePreview.title, author: "", createdAt: new Date().toISOString() }]).slice(0, 6).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={item.url === activeImagePreview.url ? "active" : ""}
                  onClick={() => setActiveImagePreview({ url: item.url, title: item.title })}
                >
                  <img src={item.url} alt="" />
                </button>
              ))}
            </div>
            <div className="media-caption">
              <strong>{imageAttachments[activeImageIndex]?.author || "Мария Смирнова"}</strong>
              <span>{imageAttachments[activeImageIndex]?.createdAt ? `сегодня в ${formatTime(imageAttachments[activeImageIndex].createdAt)}` : activeImagePreview.title}</span>
              <button type="button" aria-label="Переслать"><Icon name="forward" /></button>
              <button type="button" aria-label="Удалить"><Icon name="trash" /></button>
            </div>
          </footer>
        </div>
      )}

      {toastMessage && <div className="chat-toast" role="status">{toastMessage}</div>}
    </div>
  );
};

export default ChatsPage;
